import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { BPM_PRESETS, CAMELOT_WHEEL, EMPTY_ATTRS, MUSICAL_KEYS, hasAttrs, useAttrAvailability } from "@/lib/musicAttrs";

const pill = "rounded-full border px-2.5 py-1 text-[10px] font-mono tracking-widest transition-colors whitespace-nowrap";
const on = "border-red-brand bg-red-brand/15 text-white";
const off = "border-white/15 text-white/60 hover:border-white/40 hover:text-white";
const box = "w-14 bg-black/40 border border-white/15 focus:border-red-brand focus:outline-none rounded-full px-2 py-1 text-[11px] text-white text-center";

function BpmInputs({ value, onChange, testid }) {
  const [lo, setLo] = useState(value.bpm_min);
  const [hi, setHi] = useState(value.bpm_max);
  useEffect(() => { setLo(value.bpm_min); setHi(value.bpm_max); }, [value.bpm_min, value.bpm_max]);
  const commit = () => onChange({ ...value, bpm_min: lo, bpm_max: hi });
  const field = (v, set, name) => (
    <input data-testid={`${testid}-bpm-${name}`} inputMode="numeric" placeholder={name} value={v} aria-label={`${name} BPM`}
      onChange={(e) => set(e.target.value.replace(/[^\d]/g, "").slice(0, 3))} onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && commit()} className={box} />
  );
  return (
    <div className="flex items-center gap-1 text-[10px] font-mono tracking-widest text-white/50" dir="ltr">
      BPM {field(lo, setLo, "min")} – {field(hi, setHi, "max")}
    </div>
  );
}

function Picker({ label, field, options, value, onChange, testid, hint }) {
  const sel = value[field];
  const toggle = (c) => {
    const next = sel.includes(c) ? sel.filter((x) => x !== c) : [...sel, c];
    const any = next.length || value[field === "keys" ? "camelot" : "keys"].length;
    onChange({ ...value, [field]: next, compatible: any ? value.compatible : false });
  };
  const shown = options.filter((o) => sel.includes(o.code)).map((o) => o.label);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`${testid}-trigger`} className={`${pill} inline-flex items-center gap-1 ${sel.length ? on : off}`}>
          {sel.length ? `${label}: ${shown.slice(0, 3).join(", ")}${shown.length > 3 ? ` +${shown.length - 3}` : ""}` : label}
          <ChevronDown size={11} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] bg-[#0b0b0d] border-white/15 p-3" data-testid={`${testid}-panel`}>
        <div className="grid grid-cols-6 gap-1">
          {options.map((o) => (
            <button key={o.code} type="button" onClick={() => toggle(o.code)} data-testid={`${testid}-${o.code}`} title={o.title}
              className={`rounded-md border px-1 py-1.5 font-mono text-[10px] text-center transition-colors ${sel.includes(o.code) ? on : off}`}>
              {o.label}
            </button>
          ))}
        </div>
        {hint && <p className="mt-2 text-[10px] text-white/45" data-testid={`${testid}-hint`}>{hint}</p>}
      </PopoverContent>
    </Popover>
  );
}

const KEY_OPTIONS = MUSICAL_KEYS.map((k) => ({ code: k.code, label: k.label, title: `${k.label} → ${k.code}` }));
const CAM_OPTIONS = CAMELOT_WHEEL.map((c) => ({ code: c.code, label: c.code, title: c.key }));

export default function BpmKeyFilter({ value, onChange, testid = "bpm-key-filter" }) {
  const { t } = useTranslation();
  const avail = useAttrAvailability();
  const hint = avail && !avail.key ? t("musicAttrs.keysPending", { defaultValue: "Keys appear once they are verified." }) : null;
  const preset = BPM_PRESETS.find((p) => String(p.min) === String(value.bpm_min) && String(p.max ?? "") === String(value.bpm_max));
  const anyKey = value.keys.length > 0 || value.camelot.length > 0;
  return (
    <div data-testid={testid} className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-2 rounded-2xl border border-white/10 bg-white/[0.015] px-3 py-2.5">
      <BpmInputs value={value} onChange={onChange} testid={testid} />
      <div className="flex flex-wrap gap-1.5">
        {BPM_PRESETS.map((p) => (
          <button key={p.label} type="button" dir="ltr" data-testid={`${testid}-preset-${p.min}`}
            onClick={() => onChange(preset === p ? { ...value, bpm_min: "", bpm_max: "" } : { ...value, bpm_min: String(p.min), bpm_max: p.max == null ? "" : String(p.max) })}
            className={`${pill} ${preset === p ? on : off}`}>
            {p.label}
          </button>
        ))}
      </div>
      <span className="hidden md:block h-5 w-px bg-white/10" aria-hidden />
      <Picker label={t("musicAttrs.key", { defaultValue: "KEY" })} field="keys" options={KEY_OPTIONS} value={value} onChange={onChange} testid={`${testid}-key`} hint={hint} />
      <Picker label="CAMELOT" field="camelot" options={CAM_OPTIONS} value={value} onChange={onChange} testid={`${testid}-camelot`} hint={hint} />
      <label className={`inline-flex items-center gap-1.5 text-[10px] font-mono tracking-widest ${anyKey ? "text-white/70" : "text-white/30"}`}>
        <Switch data-testid={`${testid}-compatible`} checked={value.compatible} disabled={!anyKey} className="border border-white/25 data-[state=unchecked]:bg-white/10"
          onCheckedChange={(c) => onChange({ ...value, compatible: c })} />
        {t("musicAttrs.compatible", { defaultValue: "HARMONIC MATCHES" })}
      </label>
      {hasAttrs(value) && (
        <button type="button" data-testid={`${testid}-clear`} onClick={() => onChange(EMPTY_ATTRS)}
          className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono tracking-widest text-red-brand hover:text-white">
          {t("filter.clear")} <X size={11} />
        </button>
      )}
    </div>
  );
}
